import * as React from "react";
import { Button, Card, Divider } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { GvcInput } from "../../schema/types/gvc/input";
import { parseLink } from "../../utils/linkParser/linkParser";
import { CardDescription } from "../../components/antd/card/CardDescription";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { Secret } from "../../schema/types/secret/entity";
import { getSecretTableItem, SecretTableItem } from "../../mst/kinds/secret.helpers";
import { Table } from "../../newcomponents/table/table";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableModal } from "../../components/antd/TableModal";
import { NameDescriptionNoLinkColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { WorkloadGVCTagFn } from "../../components/antd/kindselect/tagRenderers/workloadGvcTag";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormItemDescription } from "../../components/antd/form/FormItemDescription";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { formSetValue, useCpFormValue } from "./testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { CpSwitch } from "./testNewForm/CpSwitch";
import { CpFormItem } from "./testNewForm/CpFormItem";
import { CpLabel } from "./testNewForm/CpLabel";
import { CpDescription } from "./testNewForm/CpDescription";
import { CpErrorWarning } from "./testNewForm/CpErrorWarning";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";
import { CpKindSelect } from "./testNewForm/temp/CpKindSelect";
import { CpTooltip } from "./testNewForm/CpTooltip";
import { CpLabelTooltip } from "./testNewForm/CpLabelTooltip";

interface Props {}
export const Keda: React.FC<Props> = ({}) => {
  const org = useAppSelector(selectOrg);

  const { id } = useCpFormContext();

  const name = useCpFormValue(`name`);

  const isEnabled = useCpFormValue(`keda.isEnabled`);
  const isEdit = useCpFormValue(`meta.isEdit`);

  // secret items START
  const { kind: addTableKind, id: addTableId } = useTableKindId("secret", "gvc-keda-secret-items");
  const qData = useTableItemQueryData<Secret>(addTableKind, {});

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<SecretTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, SecretTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const secrets = useCpFormValue(`keda.secrets`);
  const secretTableItems: SecretTableItem[] = React.useMemo(() => secrets.map((s) => getSecretTableItem(s)), [secrets]);

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: GvcInput = form.values as any;

    const currentSecrets: KindSelectState[] = cloneDeep(values.keda.secrets);

    currentSecrets.splice(index, 1);
    formSetValue(id, `keda.secrets`, currentSecrets);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: GvcInput = form.values as any;

    const currentSecrets: KindSelectState[] = cloneDeep(values.keda.secrets);

    // TODO selectionsToAdd can be renamed to something with link
    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentSecrets.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentSecrets.push({
        value: name,
        link: selectionToAdd,
      });
    }
    // TODO was sorting the links but no more because now it is kindselectstate

    // TODO initialize this item cache on load for detail page
    setItemCache(itemCache);

    formSetValue(id, `keda.secrets`, currentSecrets);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);
  }

  const currentLinks = secrets.map((s) => s.link);
  const addSecretTableItems: SecretTableItem[] = qData.visibleItems
    .map((item) => getSecretTableItem({ item: item }))
    .filter((item) => !currentLinks.includes(item.selfLink));
  // secret items END

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>KEDA Autoscaling</CardTitle>
          <CardDescription>
            KEDA is a Kubernetes-based event-driven autoscaler that allows you to scale workloads based on external
            events.
          </CardDescription>

          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`keda.isEnabled`}>Enable KEDA</CpLabel>
                <CpSwitch name={`keda.isEnabled`} />
                <CpTooltip
                  tooltipProps={{
                    title: `Enable KEDA for this GVC. When enabled, a keda operator will be deployed in the GVC and workloads in the
                GVC can use KEDA to scale based on external metrics.`,
                  }}
                />
              </div>
              <CpErrorWarning name={`keda.isEnabled`} />
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              {isEdit ? (
                <CpFormItem>
                  <CpLabelTooltip>
                    <CpLabel name={`keda.identity`}>Identity</CpLabel>
                    <CpTooltip
                      tooltipProps={{
                        title: `A link to an Identity resource that will be used for KEDA. This will allow the keda operator to
                    access cloud and network resources.`,
                      }}
                    />
                  </CpLabelTooltip>
                  <CpKindSelect
                    optionValue="name"
                    kind="identity"
                    style={{ width: 400 }}
                    placeholder={"Select or type an Identity Item"}
                    name={`keda.identity`}
                    tagRenderers={[WorkloadGVCTagFn(true)]}
                    queries={[{ rel: "gvc", value: name }]}
                  />
                  <CpErrorWarning name={`keda.identity`} />
                </CpFormItem>
              ) : (
                <div className="mb-12">
                  <FormItem>
                    <div>Identity</div>
                    <FormItemDescription>Identity can be set after the creation of the GVC.</FormItemDescription>
                  </FormItem>
                </div>
              )}
              <Divider orientation={"left"} orientationMargin={0}>
                Secrets
              </Divider>
              {secretTableItems.length > 0 ? (
                <div className="-mt-4">
                  <Table<SecretTableItem>
                    tableId="gvc-keda-secret-items-editable"
                    data={secretTableItems}
                    hideSettings
                    columns={[
                      NameDescriptionNoLinkColumn({ enableSort: false }),
                      TagsColumn(),
                      CreatedColumn(),
                      LastModifiedColumn(),
                      {
                        id: "action",
                        label: "",
                        enableResize: false,
                        size: 120,
                        cell: ({ row }) => {
                          return (
                            <div className="w-full flex justify-end">
                              <Button
                                htmlType={`button`}
                                type={"text"}
                                icon={<CloseOutlined />}
                                danger
                                onClick={() => onRemoveItem(row.index)}
                              />
                            </div>
                          );
                        },
                      },
                    ]}
                  />
                </div>
              ) : null}
              <TableModal
                open={isAddingItem}
                onCancel={() => setIsAddingItem(false)}
                onOk={() => {
                  onOkAddModal();
                }}
              >
                <Table<SecretTableItem>
                  tableId={addTableId}
                  title={"Select Secrets to Add"}
                  selectMode="multi"
                  selectKey="selfLink"
                  selections={selectionsToAdd}
                  onSelectionsChange={(keys, items) => {
                    setSelectionsToAdd(keys);
                    setSelectionItems(items!);
                  }}
                  data={addSecretTableItems}
                  headerRenderer={() => {
                    return (
                      <>
                        <TableHeaderRefreshButton onClick={qData.fetchItems} />
                        <CustomTableCPLNQuery
                          tableId={addTableId}
                          kind={addTableKind}
                          onQuery={qData.setQuery}
                          isLoading={qData.isLoadingQueried}
                        />
                      </>
                    );
                  }}
                  enableFilter
                  isLoading={qData.isLoading}
                  onSortChange={qData.setSort}
                  onSearchChange={qData.setSearch}
                  isLoadingMore={qData.isLoadingMore}
                  hasMore={qData.hasMore}
                  onLoadMore={qData.fetchNextPage}
                  columns={[
                    NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
                    TagsColumn(),
                    CreatedColumn(),
                    LastModifiedColumn(),
                  ]}
                />
              </TableModal>
              <Button
                htmlType={`button`}
                className="mt-4"
                type={"dashed"}
                icon={<PlusCircleOutlined />}
                onClick={() => setIsAddingItem(true)}
              >
                Add Secret
              </Button>
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
